import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';
import { drawRopeSpan, drawTimberBoards } from './propDrawingPrimitives';

export type QuaysideDetailTemplate = 'rope_rail_segment' | 'harbor_bollard' | 'open_supply_crate' | 'market_goods_table';

/** 144×80. Two slack spans remain open underneath; the posts carry the feet. */
function ropeRail(p: TownPropPalette): string {
  const posts = [6, 122].map(x => `<g data-part="rail-post">
    ${drawTimberBoards({ x, y: 9, width: 16, height: 71 }, p, { axis: 'vertical', boards: 2 })}
    <path d="M${x - 3} 5H${x + 19}V13H${x - 3}Z" fill="${hex(p.iron)}" />
    <path d="M${x - 1} 6H${x + 17}" stroke="${hex(p.stone)}" stroke-width="1.2" />
    <path d="M${x - 3} 73H${x + 19}V80H${x - 3}Z" fill="${hex(p.iron)}" />
    <circle cx="${x + 8}" cy="18" r="2" fill="${hex(p.stone)}" />
    <path d="M${x - 1} 22H${x + 18} M${x - 1} 26H${x + 18}
      M${x - 1} 47H${x + 18} M${x - 1} 51H${x + 18}"
      stroke="${hex(p.woodEdge)}" stroke-width="2" />
  </g>`).join('');
  return `<g data-part="rope-rail">
    ${drawRopeSpan({ x: 14, y: 23 }, { x: 130, y: 23 }, p, { sag: 15, thickness: 4 })}
    ${drawRopeSpan({ x: 14, y: 49 }, { x: 130, y: 49 }, p, { sag: 13, thickness: 3.5 })}
    ${posts}
  </g>`;
}

/** 60×60. The coil wraps a timber core beneath an iron cap and above its plinth. */
function bollard(p: TownPropPalette): string {
  const coils = [35, 40, 45].map(y => `<ellipse cx="30" cy="${y}" rx="21" ry="6"
    fill="none" stroke="${hex(p.woodEdge)}" stroke-width="2.6" />`).join('');
  return `<g data-part="harbor-bollard">
    <path d="M10 51L16 47H44L51 51V60H8V54Z" fill="${hex(p.stone)}" />
    <path d="M8 56H51V60H8Z" fill="${hex(p.iron)}" />
    ${drawTimberBoards({ x: 21, y: 12, width: 18, height: 39 }, p, { axis: 'vertical', boards: 2 })}
    <path d="M21 13H39V20H21Z M20 42H40V51H20Z" fill="${hex(p.iron)}" />
    <path d="M16 8H44V16Q30 23 16 16Z" fill="${hex(p.iron)}" />
    <ellipse cx="30" cy="8" rx="14" ry="4" fill="${hex(p.stone)}" />
    <ellipse cx="30" cy="8" rx="10" ry="2" fill="${hex(p.iron)}" />
    <path d="M24 25V30 M35 24V28" stroke="${hex(p.primary)}" stroke-width="2" />
    <g data-part="mooring-coils">${coils}</g>
    ${drawRopeSpan({ x: 48, y: 43 }, { x: 55, y: 55 }, p, { sag: 2, thickness: 2.6 })}
    <path d="M15 53H24 M34 53H45" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
  </g>`;
}

/** 88×76. The dark open mouth shows its back wall and floor above three front slats. */
function crate(p: TownPropPalette, context?: string): string {
  const front = [33, 47, 61].map(y => drawTimberBoards(
    { x: 10, y, width: 55, height: 11 }, p, { axis: 'horizontal', boards: 1 },
  )).join('');
  let tray = '';
  if (
    context === 'drowned_road_hush_nook' ||
    context === 'fitted-flat-tray' ||
    (p as any).surface === 'fitted-flat-tray' ||
    (p as any).variant === 'fitted-flat-tray'
  ) {
    tray = `<g data-part="fitted-flat-tray">
      ${drawTimberBoards({ x: 12, y: 14, width: 8, height: 10 }, p, { axis: 'vertical', boards: 1 })}
      ${drawTimberBoards({ x: 70, y: 14, width: 8, height: 8 }, p, { axis: 'vertical', boards: 1 })}
      ${drawTimberBoards({ x: 4, y: 6, width: 80, height: 14 }, p, { axis: 'horizontal', boards: 1 })}
      <path d="M4 6H84" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    </g>`;
  }
  return `<g data-part="open-supply-crate">
    <path d="M10 20L30 8L80 16V61L65 72H10Z" fill="${hex(p.wood)}" />
    <path d="M16 22L31 13L74 19L61 30Z" fill="${hex(p.iron)}" />
    <path d="M16 22L31 13V21L25 25Z" fill="${hex(p.shade)}" />
    <path d="M25 25L31 21L67 24L61 30Z" fill="${hex(p.shade)}" />
    <path data-part="crate-visible-interior" d="M31 14L69 19L53 25L31 21Z" fill="${hex(p.iron)}" />
    <path d="M10 20L30 8L80 16L77 21L30 13L15 23Z" fill="${hex(p.woodEdge)}" />
    <path d="M65 29L80 19V63L65 73Z" fill="${hex(p.shade)}" />
    <path d="M67 34L78 26 M67 48L78 40 M67 62L77 55" stroke="${hex(p.woodEdge)}" stroke-width="2" />
    <path d="M12 30H64V69H12Z" fill="${hex(p.iron)}" />
    ${front}
    <path d="M8 27L65 29L69 34H8Z" fill="${hex(p.woodEdge)}" />
    <path d="M9 32H16V72H9Z M58 33H65V72H58Z" fill="${hex(p.woodEdge)}" />
    <path d="M11 35H14 M60 36H63 M11 66H14 M60 66H63" stroke="${hex(p.iron)}" stroke-width="2" />
    <path d="M9 71H23V76H9Z M54 71H68V76H54Z" fill="${hex(p.wood)}" />
    <path d="M21 53L27 49L34 53L28 56Z" fill="${hex(p.primary)}" opacity=".8" />
    ${tray}
  </g>`;
}

function sack(x: number, y: number, p: TownPropPalette): string {
  return `<g data-part="market-sack" transform="translate(${x} ${y})">
    <path d="M7 31Q0 22 7 12L13 7L11 0H25L23 7Q36 19 30 31Z" fill="${hex(p.stone)}" />
    <path d="M23 8Q30 23 24 31H30Q36 19 23 8Z" fill="${hex(p.shade)}" />
    <path d="M12 6H24 M9 17L11 28 M16 11L15 22 M20 2L19 6" fill="none"
      stroke="${hex(p.woodEdge)}" stroke-width="1.3" />
  </g>`;
}

/** 148×98. A layered cloth and varied provisions sit on a clearly open-legged table. */
function goodsTable(p: TownPropPalette): string {
  const legs = [25, 111].map(x => `<g data-part="table-leg">
    ${drawTimberBoards({ x, y: 53, width: 11, height: 45 }, p, { axis: 'vertical', boards: 1 })}
    <path d="M${x - 2} 94H${x + 13}V98H${x - 2}Z" fill="${hex(p.iron)}" />
  </g>`).join('');
  return `<g data-part="market-goods-table">
    ${legs}
    <path d="M33 60L53 76 M114 60L94 76" stroke="${hex(p.wood)}" stroke-width="6" />
    <path d="M33 60L53 76 M114 60L94 76" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
    ${drawTimberBoards({ x: 6, y: 46, width: 136, height: 12 }, p, { axis: 'horizontal', boards: 2 })}
    <path d="M6 46L14 41H134L142 46Z" fill="${hex(p.woodEdge)}" />
    <path data-part="market-cloth" d="M18 42H81L89 48L86 70L68 68L55 72L35 66L17 69L14 49Z" fill="${hex(p.primary)}" />
    <path d="M18 49L23 65 M35 45L39 64 M66 46L65 65 M80 49L78 65" stroke="${hex(p.shade)}" stroke-width="1.4" />
    <path d="M18 64L35 61L55 67L68 63L85 65" fill="none" stroke="${hex(p.glassCore)}" stroke-width="1.3" />
    ${sack(17, 10, p)}${sack(46, 13, p)}
    <g data-part="market-basket">
      <path d="M93 34V23Q110 3 127 23V34" fill="none" stroke="${hex(p.wood)}" stroke-width="3" />
      <path d="M94 31Q110 20 128 31" fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.2" />
      <ellipse cx="110" cy="34" rx="24" ry="7" fill="${hex(p.iron)}" />
      <circle cx="99" cy="30" r="7" fill="${hex(p.glass)}" />
      <circle cx="112" cy="27" r="8" fill="${hex(p.primary)}" />
      <circle cx="124" cy="32" r="7" fill="${hex(p.glassCore)}" />
      <path d="M87 34Q110 44 134 34L129 52Q110 58 92 52Z" fill="${hex(p.wood)}" />
      <path d="M90 40Q110 48 132 40 M92 46Q111 54 130 46 M97 40L100 53 M109 43V55 M122 41L120 53"
        fill="none" stroke="${hex(p.woodEdge)}" stroke-width="1.5" />
      ${drawRopeSpan({ x: 87, y: 34 }, { x: 134, y: 34 }, p, { sag: 5, thickness: 2.5 })}
    </g>
  </g>`;
}

/** Catalog supplies frames and render bands; these static bodies create no collision or interactions. */
export function buildQuaysideDetailBody(
  template: QuaysideDetailTemplate,
  palette: TownPropPalette,
  context?: string,
): string {
  switch (template) {
  case 'rope_rail_segment': return ropeRail(palette);
  case 'harbor_bollard': return bollard(palette);
  case 'open_supply_crate': return crate(palette, context);
  case 'market_goods_table': return goodsTable(palette);
  }
}
